/**
 * Shared response-mapping utilities for manifest-driven features.
 *
 * Template grammar (used by setValues, oauthConfig.setValues, action.onSuccess.setValues):
 *   "$response"                              → the entire response object (returned as-is)
 *   "$selected"                              → alias for $response (clearer for select field setValues)
 *   "$response.a.b.c"                        → getNested(response, 'a.b.c') (returned as-is, preserves type)
 *   "$selected.a.b"                          → same as $response.a.b
 *   "prefix {{$response.x}} suffix"          → embedded tokens replaced inline, result is a string
 *   "{{$response.a}} / {{$response.b}}"      → multiple tokens supported
 *   anything else                            → the template string itself (literal)
 *
 * When any embedded token resolves to `undefined`, the whole result is `undefined` so
 * `applyResponseMapping` skips the write (prevents 'https://.../undefined' strings).
 */
import type { UseFormReturn } from 'react-hook-form';
import type {
  CompanyInfo,
  ConfigField,
  ConfigFieldOption,
  PickerSetValuesCase,
  SetValuesTemplate,
} from '@scio3/shared-types';
import { getNested } from './nested-keys';
import { shouldShow } from './display-options';
import { isReservedFormKey } from './synthetic-form-keys';

type ManifestForm = UseFormReturn<Record<string, unknown>>;

const EMBEDDED_TOKEN_RE = /\{\{\$response(\.[a-zA-Z0-9_.]+)?\}\}/g;

/**
 * Resolve a responsePath against a JSON response.
 * Plain path convention: `"calendars"` extracts `json.calendars`, `""` or undefined returns root.
 * No `$response` prefix — plain dot-notation paths only.
 */
export function resolveResponsePath(responsePath: string | undefined, json: unknown): unknown {
  if (!responsePath) return json;
  return getNested(json as Record<string, unknown>, responsePath);
}

export function interpolateResponse(template: SetValuesTemplate, response: unknown): unknown {
  // Typed literal pass-through. `null` is a valid form value (e.g. clearing a
  // field); `0` / `false` are valid numeric / boolean targets. Only strings
  // flow through the template parser.
  if (typeof template !== 'string') return template;
  if (template === '$response' || template === '$selected') return response;
  if (template.startsWith('$response.') || template.startsWith('$selected.')) {
    const path = template.startsWith('$response.')
      ? template.slice('$response.'.length)
      : template.slice('$selected.'.length);
    return getNested(response as Record<string, unknown>, path);
  }
  if (template.includes('{{$response')) {
    let hasUndefined = false;
    const out = template.replace(EMBEDDED_TOKEN_RE, (_match, dotPath: string | undefined) => {
      const resolved = dotPath
        ? getNested(response as Record<string, unknown>, dotPath.slice(1))
        : response;
      if (resolved === undefined) {
        hasUndefined = true;
        return '';
      }
      return typeof resolved === 'string' ? resolved : JSON.stringify(resolved);
    });
    return hasUndefined ? undefined : out;
  }
  return template;
}

/** Apply a `Record<formKey, template-or-literal>` mapping to a react-hook-form
 *  instance. Strings flow through `interpolateResponse`; non-string entries
 *  (number, boolean, null) are written verbatim as typed literals.
 *  Keys may be dotted paths (handled natively by RHF setValue).
 *  Undefined resolutions are skipped so stale values aren't clobbered with undefined. */
export function applyResponseMapping(
  form: ManifestForm,
  mapping: Record<string, SetValuesTemplate>,
  response: unknown,
  /** `onlyUnset` — only write keys whose current form value is unset (undefined/null/'').
   *  Used on EDIT-mode asset re-resolve so derived fields (e.g. a captured fileType) populate
   *  without clobbering persisted/user-set values. Writes are non-dirtying in this mode so
   *  hydration doesn't trip the wizard's skip-save (#641).
   *
   *  `dirty` — does this write count as a user edit? Load-time derives pass `false`: re-deriving
   *  a preset when a saved asset opens is hydration, not an edit, so it must not enable Save.
   *  Independent of validation, which always follows `onlyUnset`. */
  opts?: { onlyUnset?: boolean; dirty?: boolean },
): void {
  const onlyUnset = opts?.onlyUnset ?? false;
  const dirty = opts?.dirty ?? !onlyUnset;
  const current = onlyUnset ? (form.getValues() as Record<string, unknown>) : null;
  for (const [fieldKey, template] of Object.entries(mapping)) {
    if (current) {
      const cur = getNested(current, fieldKey);
      if (cur !== undefined && cur !== null && cur !== '') continue;
    }
    const resolved = interpolateResponse(template, response);
    if (resolved === undefined) continue;
    form.setValue(fieldKey, resolved as never, {
      shouldDirty: dirty,
      shouldValidate: !onlyUnset,
    });
  }
}

/** Find the first `PickerSetValuesCase` whose `when` matches.
 *
 *  `when` keys are evaluated against a virtual namespace that overlays the picked
 *  asset on top of current form values: keys starting with `$selected.` resolve
 *  off the picked asset (e.g. `$selected.appType` → `picked.appType`), all other
 *  keys resolve as normal form refs (existing field keys, dot-notation, external
 *  `account.*` / `system.*` / `feature.*` via `shouldShow`).
 *
 *  Why merge as `$selected`: matches the existing template grammar in `values`
 *  (`$selected.appType` reads off the picked asset), so author mental model is
 *  one rule — `$selected.*` always means "the picked entity" everywhere in
 *  picker setValues. No separate staging-into-hidden-field dance.
 *
 *  A case with no `when` (or empty `when`) is an unconditional fallback. */
export function pickWinningCase(
  cases: PickerSetValuesCase[],
  formValues: Record<string, unknown>,
  picked?: unknown,
  company?: CompanyInfo | null,
): PickerSetValuesCase | undefined {
  // Overlay the picked entity as `$selected` so `getNested` walks it via the
  // existing form-key resolution path. `getNested` parses dotted keys via
  // `split('.')` so `"$selected.appType"` → `['$selected', 'appType']` →
  // `virtual['$selected']['appType']` → `picked.appType`. No engine-wide change
  // needed; shouldShow remains pure form/company-key resolution.
  const virtualValues = picked !== undefined ? { ...formValues, $selected: picked } : formValues;
  return cases.find((c) =>
    shouldShow({ displayOptions: c.when ? { showIf: c.when } : undefined }, virtualValues, company),
  );
}

/** Apply picker-style `setValues` to a form. Handles both shapes:
 *   - `Record<string, string>` — fan picked-asset metadata verbatim into form keys.
 *   - `PickerSetValuesCase[]` — first-match-wins. Each case's `when` is evaluated
 *     against current form values overlaid with `$selected` = picked asset, so
 *     `when` refs may use form keys, external `account.*` / `system.*` / `feature.*`,
 *     OR `$selected.<path>` to read off the picked asset directly. The first
 *     matching case's `values` are applied (templates resolve against the picked
 *     asset). Skip silently if no case matches.
 *  No-op when `setValues` is undefined. */
export function applyPickerSetValues(
  form: ManifestForm,
  setValues: ConfigField['setValues'],
  asset: unknown,
  company?: CompanyInfo | null,
  /** Both forwarded verbatim to `applyResponseMapping` — see its docs.
   *  `onlyUnset`: edit-mode re-resolve, hydrate derived fields without overwriting persisted/user
   *  values. `dirty`: whether the write counts as a user edit (load-time derives pass `false`). */
  opts?: { onlyUnset?: boolean; dirty?: boolean },
): void {
  if (!setValues) return;
  const onlyUnset = opts?.onlyUnset ?? false;
  const dirty = opts?.dirty ?? !onlyUnset;
  if (Array.isArray(setValues)) {
    const currentValues = form.getValues() as Record<string, unknown>;
    const winner = pickWinningCase(setValues, currentValues, asset, company);
    if (winner) applyResponseMapping(form, winner.values, asset, { onlyUnset, dirty });
    return;
  }
  applyResponseMapping(form, setValues, asset, { onlyUnset, dirty });
}

/** The part of a `setValues` mapping that targets UI-only keys: `isReservedFormKey`, the same rule
 *  the save payload is stripped by. Those keys are never saved, so a reopened asset arrives without
 *  them, and they are the only targets a select may refill when the form opens
 *  (`useUiOnlySetValuesOnOpen`). Same shape out as in. A cases array keeps every case and its
 *  `when`, so first-match-wins still picks the case it would have picked; a winner with no UI-only
 *  target then writes nothing. Undefined when no target is UI-only. */
export function uiOnlySetValues(setValues: ConfigField['setValues']): ConfigField['setValues'] {
  if (!setValues) return undefined;
  const pick = (values: Record<string, SetValuesTemplate>) =>
    Object.fromEntries(Object.entries(values).filter(([key]) => isReservedFormKey(key)));
  if (Array.isArray(setValues)) {
    const cases = setValues.map((c) => ({ ...c, values: pick(c.values) }));
    return cases.some((c) => Object.keys(c.values).length > 0) ? cases : undefined;
  }
  const values = pick(setValues);
  return Object.keys(values).length > 0 ? values : undefined;
}

/** Clear every target key a `setValues` mapping would populate — set each to ''.
 *  The inverse of `applyPickerSetValues`: when an optional picker is cleared, its
 *  derived siblings (e.g. an S3 key / bucket / url captured off the picked asset)
 *  must not retain a stale value pointing at the removed asset. Mirrors that
 *  function's key set — Record → its keys; `cases[]` → the union of every case's
 *  `values` keys. Writes '' (not undefined) so RHF actually resets a controlled
 *  field, and dirties the form so the wizard's save is not skipped. */
export function clearPickerSetValues(
  form: ManifestForm,
  setValues: ConfigField['setValues'],
): void {
  if (!setValues) return;
  const keys = Array.isArray(setValues)
    ? [...new Set(setValues.flatMap((c) => Object.keys(c.values)))]
    : Object.keys(setValues);
  for (const key of keys) {
    form.setValue(key, '' as never, { shouldDirty: true, shouldValidate: true });
  }
}

/**
 * Extract and map options from an API response using fetchOptions config.
 *
 * Supports:
 * - `responsePath` — `$response.path` or plain path to extract the array from nested responses
 * - `responseMap` — map response item fields to { label, value }
 * - Filters out items with empty label or null/undefined value
 */
export const parseResponseOptions = (
  json: unknown,
  config: NonNullable<ConfigField['fetchOptions']>,
): ConfigFieldOption[] => {
  const rawArray = resolveResponsePath(config.responsePath, json);
  const items = Array.isArray(rawArray)
    ? rawArray
    : ((rawArray as Record<string, unknown>)?.options ?? []);

  // Map fields if configured, skip items with empty label or null/undefined value
  const { responseMap } = config;
  if (responseMap) {
    const { label: labelKey, value: valueKey, ...extraKeys } = responseMap;
    return (items as Record<string, unknown>[])
      .map((item) => {
        const opt: Record<string, unknown> = {
          label: String(item[labelKey] ?? ''),
          value: item[valueKey] as string | number | boolean,
        };
        for (const [optKey, itemKey] of Object.entries(extraKeys)) {
          opt[optKey] = item[itemKey];
        }
        return opt as ConfigFieldOption & Record<string, unknown>;
      })
      .filter((opt) => opt.label && opt.value != null);
  }

  return Array.isArray(items)
    ? items.filter((c): c is ConfigFieldOption => !!c && typeof c === 'object' && 'label' in c)
    : [];
};
